import type React from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { Button } from 'ui-components/components/button';
import type { ButtonThemeValues } from 'ui-components/components/button/button-types';
import { noop } from 'ui-components/default-props/noop';

import { getSSRSingleSignOnToken } from '../../../../../../store/returns/returns.api';
import { ReturnsConfigIframeModal } from './returns-config-iframe-modal';
import messages from './messages';

type SsrConfigIframeButtonProps = {
  path?: string;
  storeGuid: string;
  theme?: ButtonThemeValues;
  children: React.ReactNode;
  onModalClosed?: () => void;
};

export const SsrConfigIframeButton = ({
  path = '',
  storeGuid,
  theme = 'tertiary',
  children = null,
  onModalClosed = noop,
}: SsrConfigIframeButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  const onOpenIframe = useCallback(async () => {
    try {
      const response = await getSSRSingleSignOnToken(storeGuid);
      if (response?.token) {
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        const host = (window as any).SS_GLOBALS.frontEndConfig.ssrConfigIframeHost;
        let redirectUrl = `${host}/seller-signin?token=${response.token}&store_guid=${storeGuid}`;
        if (path !== '') {
          redirectUrl += `&redirect_path=${encodeURIComponent(path)}`;
        }
        setIframeUrl(redirectUrl);
        setIsModalOpen(true);
      }
    } catch {
      NotificationService.showNotification({
        content: <FormattedMessage {...messages.ssoTokenError} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    }
  }, [path, storeGuid]);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setIframeUrl('');
    onModalClosed();
  }, [onModalClosed]);

  return (
    <>
      <Button onClick={onOpenIframe} theme={theme}>
        {children}
      </Button>
      <ReturnsConfigIframeModal iframeUrl={iframeUrl} isOpen={isModalOpen} onClose={onCloseModal} />
    </>
  );
};
